import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ModuleConfig } from './useModuleLayout';

interface ModuleGridProps {
  modules: ModuleConfig[];
  onDragEnd: (activeId: string, overId: string) => void;
  renderModule: (module: ModuleConfig, dragHandleProps: Record<string, unknown>) => React.ReactNode;
}

interface SortableModuleItemProps {
  module: ModuleConfig;
  renderModule: ModuleGridProps['renderModule'];
}

const SortableModuleItem: React.FC<SortableModuleItemProps> = ({ module, renderModule }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    gridColumn: module.span === 'full' ? '1 / -1' : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="min-w-0">
      {renderModule(module, { ...attributes, ...listeners })}
    </div>
  );
};

const ModuleGrid: React.FC<ModuleGridProps> = ({ modules, onDragEnd, renderModule }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onDragEnd(String(active.id), String(over.id));
    }
  };

  const moduleIds = modules.map(m => m.id);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {modules.map(module => (
            <SortableModuleItem
              key={module.id}
              module={module}
              renderModule={renderModule}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default ModuleGrid;
